import { motion } from 'framer-motion';
import { Sparkles, Clock, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const stats = [
  { label: 'Same-Day Response', value: '2hr' },
  { label: 'Vehicles Renowned', value: '1,200+' },
  { label: 'Satisfaction', value: '100%' },
];

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-24 pb-16 sm:pb-20 lg:pb-28">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(215,38,61,0.25),transparent_60%)]" />
      <div className="mx-auto flex max-w-6xl flex-col gap-12 px-4 lg:flex-row lg:items-center">
        <div className="flex-1 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
              Mobile Detailing • GTA
            </span>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.5rem]">
              We bring showroom-level detailing
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-200 via-brand-500 to-rose-400">
                directly to your driveway.
              </span>
            </h1>
            <p className="max-w-xl text-base text-white/70 sm:text-lg">
              Pick the package that matches your vehicle size, condition, and goals. We follow a
              proven five-step system so pricing stays clear and approvals are instant.
            </p>
          </motion.div>

          <motion.div
            className="grid gap-4 sm:grid-cols-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-white/5 bg-white/5 px-4 py-4 text-center shadow-lg shadow-brand-900/40"
              >
                <p className="text-3xl font-semibold text-white">{stat.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          <motion.div
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-900/40 transition hover:scale-[1.02] hover:bg-brand-400"
            >
              <Sparkles size={16} />
              Start A Booking
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-3 text-sm font-semibold text-white/80 transition hover:border-white/40 hover:text-white"
            >
              Explore Packages
            </Link>
          </motion.div>

          <motion.ul
            className="flex flex-col gap-4 text-sm text-white/70 sm:flex-row sm:flex-wrap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
          >
            <li className="flex items-center gap-3 rounded-full border border-white/10 px-5 py-3">
              <ShieldCheck size={18} className="text-brand-200" />
              Licensed & insured pros
            </li>
            <li className="flex items-center gap-3 rounded-full border border-white/10 px-5 py-3">
              <Clock size={18} className="text-brand-200" />
              Flexible scheduling
            </li>
            <li className="flex items-center gap-3 rounded-full border border-white/10 px-5 py-3">
              <Sparkles size={18} className="text-brand-200" />
              Eco-safe products
            </li>
          </motion.ul>
        </div>

        <motion.div
          className="flex-1"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.5 }}
        >
          <div className="relative mx-auto max-w-md overflow-hidden rounded-[28px] border border-brand-500/20 bg-gradient-to-br from-brand-900/40 via-brand-900/20 to-brand-800/20 p-4 shadow-2xl shadow-black/40 backdrop-blur">
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-brand-400/40 blur-3xl" />
            <div className="absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-rose-400/30 blur-3xl" />
            <div className="relative space-y-6 rounded-[20px] border border-white/5 bg-[#1c0d11]/70 p-6">
              <h2 className="text-xl font-semibold text-white">Mobile Setup Ready</h2>
              <p className="text-sm text-white/60">
                Our crew arrives with water, power, and lighting. Condo or curbside, we protect every
                surface and leave nothing behind except a perfect finish.
              </p>
              <div className="flex items-center justify-between rounded-2xl bg-white/5 p-4 text-white">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-white/50">Next opening</p>
                  <p className="text-2xl font-semibold">Tomorrow · 9:00 AM</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/80 text-white shadow-inner shadow-black/30">
                  <Clock size={22} />
                </div>
              </div>
              <p className="text-xs text-white/45">
                Need fleet or commercial service? <span className="text-brand-200">Text us for a custom quote.</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
